/*
 * kcc-hero.js — carousel behaviour for sections/kcc-hero.liquid
 * Port of the state machine in ai-workflow/src/components/kona-crown/KCCHero.tsx:
 *   - autoplay every 5s (section setting, data-interval in ms)
 *   - goTo() fades the copy out for 500ms (data-fade), then swaps the active
 *     slide, which cross-fades the background over 1s via CSS
 *   - pauses on hover, on keyboard focus, when the tab is hidden and when the
 *     hero is scrolled out of view
 *   - prefers-reduced-motion: no autoplay, no fade delay
 * Vanilla JS, no dependencies.
 */
(function () {
  'use strict';

  var ACTIVE_CONTENT = ['opacity-100', 'translate-y-0', 'pointer-events-auto'];
  var INACTIVE_CONTENT = ['opacity-0', 'translate-y-3', 'pointer-events-none'];
  var ACTIVE_DOT = ['w-10', 'bg-kcc-gold'];
  var INACTIVE_DOT = ['w-5', 'bg-white/30', 'hover:bg-white/50'];

  var instances = new Map();

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function setClasses(el, add, remove) {
    if (!el) return;
    remove.forEach(function (c) {
      el.classList.remove(c);
    });
    add.forEach(function (c) {
      el.classList.add(c);
    });
  }

  function KCCHero(root) {
    this.root = root;
    this.backgrounds = Array.prototype.slice.call(root.querySelectorAll('[data-kcc-hero-bg]'));
    this.contents = Array.prototype.slice.call(root.querySelectorAll('[data-kcc-hero-content]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-kcc-hero-dot]'));
    this.live = root.querySelector('[data-kcc-hero-live]');
    this.prevBtn = root.querySelector('[data-kcc-hero-prev]');
    this.nextBtn = root.querySelector('[data-kcc-hero-next]');

    this.count = this.contents.length;
    this.index = 0;
    this.timers = [];
    this.autoTimer = null;
    this.transitioning = false;
    this.hovered = false;
    this.focused = false;
    this.offscreen = false;
    this.editorHold = false;

    this.autoplayEnabled = root.getAttribute('data-autoplay') === 'true';
    this.interval = parseInt(root.getAttribute('data-interval'), 10) || 5000;
    this.fade = parseInt(root.getAttribute('data-fade'), 10) || 500;

    this.bound = [];
    this.init();
  }

  KCCHero.prototype.on = function (target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.bound.push([target, type, handler, options]);
  };

  KCCHero.prototype.init = function () {
    var self = this;
    if (this.count < 2) return;

    if (this.prevBtn) {
      this.on(this.prevBtn, 'click', function () {
        self.prev(true);
      });
    }
    if (this.nextBtn) {
      this.on(this.nextBtn, 'click', function () {
        self.next(true);
      });
    }

    this.dots.forEach(function (dot) {
      self.on(dot, 'click', function () {
        self.goTo(parseInt(dot.getAttribute('data-index'), 10), true);
      });
    });

    // Keyboard: arrow keys move through slides while focus is inside the hero.
    this.on(this.root, 'keydown', function (event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        self.prev(true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        self.next(true);
      }
    });

    // Pause on hover / focus.
    this.on(this.root, 'mouseenter', function () {
      self.hovered = true;
      self.stopAutoplay();
    });
    this.on(this.root, 'mouseleave', function () {
      self.hovered = false;
      self.startAutoplay();
    });
    this.on(this.root, 'focusin', function () {
      self.focused = true;
      self.stopAutoplay();
    });
    this.on(this.root, 'focusout', function () {
      self.focused = false;
      self.startAutoplay();
    });

    this.on(document, 'visibilitychange', function () {
      if (document.hidden) {
        self.stopAutoplay();
      } else {
        self.startAutoplay();
      }
    });

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            self.offscreen = !entry.isIntersecting;
            if (self.offscreen) {
              self.stopAutoplay();
            } else {
              self.startAutoplay();
            }
          });
        },
        { threshold: 0.15 }
      );
      this.observer.observe(this.root);
    }

    this.startAutoplay();
  };

  KCCHero.prototype.paused = function () {
    return this.hovered || this.focused || this.offscreen || this.editorHold || document.hidden;
  };

  KCCHero.prototype.startAutoplay = function () {
    var self = this;
    this.stopAutoplay();
    if (!this.autoplayEnabled || this.count < 2) return;
    if (prefersReducedMotion() || this.paused()) return;
    this.autoTimer = window.setInterval(function () {
      self.next(false);
    }, this.interval);
  };

  KCCHero.prototype.stopAutoplay = function () {
    if (this.autoTimer) {
      window.clearInterval(this.autoTimer);
      this.autoTimer = null;
    }
  };

  KCCHero.prototype.next = function (fromUser) {
    this.goTo((this.index + 1) % this.count, fromUser);
  };

  KCCHero.prototype.prev = function (fromUser) {
    this.goTo((this.index - 1 + this.count) % this.count, fromUser);
  };

  KCCHero.prototype.goTo = function (index, fromUser) {
    var self = this;
    if (isNaN(index) || index === this.index || this.transitioning) return;
    if (index < 0 || index >= this.count) return;

    this.transitioning = true;
    var previous = this.index;
    var delay = prefersReducedMotion() ? 0 : this.fade;

    // Fade the outgoing copy out first — matches the TSX `fade` state.
    this.setContentState(previous, false);

    var timer = window.setTimeout(function () {
      self.index = index;
      self.setBackground(index);
      self.setContentState(index, true);
      self.setDots(index);
      self.announce(index);
      self.transitioning = false;
      if (fromUser) self.startAutoplay();
    }, delay);
    this.timers.push(timer);
  };

  KCCHero.prototype.setBackground = function (index) {
    this.backgrounds.forEach(function (bg, i) {
      bg.style.opacity = i === index ? '1' : '0';
    });
  };

  KCCHero.prototype.setContentState = function (index, active) {
    var el = this.contents[index];
    if (!el) return;
    if (active) {
      el.removeAttribute('aria-hidden');
      setClasses(el, ACTIVE_CONTENT, INACTIVE_CONTENT);
    } else {
      el.setAttribute('aria-hidden', 'true');
      setClasses(el, INACTIVE_CONTENT, ACTIVE_CONTENT);
    }
    // Keep hidden slides out of the tab order.
    Array.prototype.forEach.call(el.querySelectorAll('a[href], button'), function (node) {
      if (active) {
        node.removeAttribute('tabindex');
      } else {
        node.setAttribute('tabindex', '-1');
      }
    });
  };

  KCCHero.prototype.setDots = function (index) {
    this.dots.forEach(function (dot, i) {
      if (i === index) {
        setClasses(dot, ACTIVE_DOT, INACTIVE_DOT);
        dot.setAttribute('aria-current', 'true');
      } else {
        setClasses(dot, INACTIVE_DOT, ACTIVE_DOT);
        dot.removeAttribute('aria-current');
      }
    });
  };

  KCCHero.prototype.announce = function (index) {
    if (!this.live) return;
    var el = this.contents[index];
    var heading = el ? el.querySelector('[data-kcc-hero-heading]') : null;
    var label = 'Slide ' + (index + 1) + ' of ' + this.count;
    if (heading && heading.textContent) {
      label += ': ' + heading.textContent.trim();
    }
    this.live.textContent = label;
  };

  KCCHero.prototype.destroy = function () {
    this.stopAutoplay();
    this.timers.forEach(function (t) {
      window.clearTimeout(t);
    });
    this.timers = [];
    if (this.observer) this.observer.disconnect();
    this.bound.forEach(function (entry) {
      entry[0].removeEventListener(entry[1], entry[2], entry[3]);
    });
    this.bound = [];
  };

  function initAll(scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll ? root.querySelectorAll('[data-kcc-hero]') : [];
    Array.prototype.forEach.call(nodes, function (el) {
      if (instances.has(el)) return;
      instances.set(el, new KCCHero(el));
    });
    if (root.matches && root.matches('[data-kcc-hero]') && !instances.has(root)) {
      instances.set(root, new KCCHero(root));
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Theme editor lifecycle.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var nodes = event.target.querySelectorAll('[data-kcc-hero]');
    Array.prototype.forEach.call(nodes, function (el) {
      var instance = instances.get(el);
      if (instance) {
        instance.destroy();
        instances.delete(el);
      }
    });
  });

  function instanceFor(node) {
    var root = node.closest ? node.closest('[data-kcc-hero]') : null;
    if (!root) {
      root = node.querySelector ? node.querySelector('[data-kcc-hero]') : null;
    }
    return root ? instances.get(root) : null;
  }

  // Selecting a slide block in the editor holds that slide on screen.
  document.addEventListener('shopify:block:select', function (event) {
    var instance = instanceFor(event.target);
    if (!instance) return;
    instance.editorHold = true;
    instance.stopAutoplay();
    var content = event.target.matches('[data-kcc-hero-content]')
      ? event.target
      : event.target.querySelector('[data-kcc-hero-content]');
    if (content) {
      instance.goTo(parseInt(content.getAttribute('data-index'), 10), false);
    }
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var instance = instanceFor(event.target);
    if (!instance) return;
    instance.editorHold = false;
    instance.startAutoplay();
  });
})();
